import { useCallback, useEffect, useState } from 'react'

import {
  AlertDialog,
  Button,
  DropdownMenu,
  EllipsisVerticalIcon,
  toast,
  Tooltip,
  useDropdownMenu,
} from '@buffer-mono/popcorn'

import { useDismissAll } from '../hooks/useDismissAll'

type ResolveAllCommentsDialogProps = {
  channelId: string
  children: React.ReactNode
  onHideTooltip: () => void
}

const ResolveAllCommentsDialog = ({
  channelId,
  children,
  onHideTooltip,
}: ResolveAllCommentsDialogProps): JSX.Element => {
  const { dismissAllByChannelId } = useDismissAll()
  const [open, setOpen] = useState(false)
  const { close } = useDropdownMenu()

  const handleResolveAllComments = useCallback(async (): Promise<void> => {
    try {
      await dismissAllByChannelId(channelId)
      setOpen(false)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to resolve all comments:', error)
      toast.error('Failed to resolve all comments')
    }
  }, [dismissAllByChannelId, channelId])

  const handleOpenChange = useCallback(
    (isOpen: boolean): void => {
      setOpen(isOpen)
      // Close dropdown when dialog closes
      if (!isOpen) {
        onHideTooltip() // Temporarily hide tooltip to prevent visual glitch
        close()
      }
    },
    [close, onHideTooltip],
  )

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialog.Trigger>{children}</AlertDialog.Trigger>
      <AlertDialog.Content>
        <AlertDialog.Title>Resolve all comments</AlertDialog.Title>
        <AlertDialog.Description>
          All new comments will be marked as resolved. You will be able to
          access them using the &apos;status&apos; filter.{' '}
          <strong>This cannot be undone.</strong>
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="tertiary">Cancel</Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="critical"
              onClick={(): void => {
                void handleResolveAllComments()
              }}
            >
              Resolve all comments
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}

type BulkActionsMenuProps = {
  channelId: string
}

const TOOLTIP_HIDE_DELAY = 200 // Prevents visual glitch when dialog opens

export const BulkActionsMenu = ({
  channelId,
}: BulkActionsMenuProps): JSX.Element => {
  const [hideTooltip, setHideTooltip] = useState(false)

  const handleHideTooltipTemporarily = useCallback((): void => {
    setHideTooltip(true)
  }, [])

  useEffect(() => {
    if (hideTooltip) {
      const timeoutId = setTimeout(
        () => setHideTooltip(false),
        TOOLTIP_HIDE_DELAY,
      )
      return (): void => clearTimeout(timeoutId)
    }
  }, [hideTooltip])

  const trigger = (
    <DropdownMenu.Trigger>
      <Button size="medium" variant="tertiary" aria-label="More actions">
        <EllipsisVerticalIcon />
      </Button>
    </DropdownMenu.Trigger>
  )

  return (
    <DropdownMenu modal={false}>
      {hideTooltip ? (
        trigger
      ) : (
        <Tooltip content="More actions" delay={1000}>
          {trigger}
        </Tooltip>
      )}
      <DropdownMenu.Content data-no-focus-lock>
        <ResolveAllCommentsDialog
          channelId={channelId}
          onHideTooltip={handleHideTooltipTemporarily}
        >
          <DropdownMenu.Item
            onSelect={(e): void => {
              // Needed for item to be able to open dialog
              e.preventDefault()
            }}
          >
            Resolve all comments
          </DropdownMenu.Item>
        </ResolveAllCommentsDialog>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
